import "./ChatDialog.scss";
import React, { useRef, useState, useEffect } from "react";
import Avatar from "../Avatar";

export default function ChatDialog({
  isActive,
  isThinking,
  askQuestion,
  chatDialog,
}) {
  const inputRef = useRef(null);
  const chatRef = useRef(null);
  const listRef = useRef(null);
  const rootRef = useRef(null);
  const [chatInput, setChatInput] = useState("");

  const isOpen = isActive === true;

  const mountEl =
    typeof document !== "undefined"
      ? document.getElementById("sigil-chatbot")
      : null;
  const logoUrl = (mountEl && mountEl.dataset && mountEl.dataset.logoUrl) || "";
  const logoLargeUrl =
    (mountEl && mountEl.dataset && mountEl.dataset.logoLargeUrl) || "";
  const headerLogo = logoLargeUrl || logoUrl;

  const scrollToBottom = () => {
    if (chatRef.current) {
      chatRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Align the top of the most recent bot response with the top of the chat
  // panel, so the reader starts at the beginning of a long answer.
  const scrollToLatestResponse = () => {
    const container = listRef.current;
    if (!container) {
      return;
    }
    const bots = container.querySelectorAll(".from-chatbot");
    const el = bots[bots.length - 1];
    if (!el) {
      scrollToBottom();
      return;
    }
    const offset =
      el.getBoundingClientRect().top - container.getBoundingClientRect().top;
    container.scrollTo({
      top: container.scrollTop + offset - 8,
      behavior: "smooth",
    });
  };

  // Keep the closed panel out of the tab order / accessibility tree, and move
  // focus into the input only when the chat opens.
  useEffect(() => {
    if (rootRef.current) {
      rootRef.current.inert = !isOpen;
      rootRef.current.setAttribute("aria-hidden", isOpen ? "false" : "true");
    }
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    // Keep focus in the input during an active conversation only.
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
    const last = chatDialog[chatDialog.length - 1];
    if (!isThinking && last && last.from === "chatbot") {
      scrollToLatestResponse();
    } else {
      scrollToBottom();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatDialog, isThinking]);

  const handleChatSubmit = (e) => {
    e.preventDefault();
    setChatInput("");
    askQuestion(chatInput);
  };

  return (
    <div
      className={"ChatDialog" + (isOpen ? "" : " minimized")}
      ref={rootRef}
      role="dialog"
      aria-label="Commonwealth Chatbot"
    >
      <div className="chat-header">
        {headerLogo && <img src={headerLogo} alt="Commonwealth ChatBot" />}
        {/* <span>Commonwealth</span>*/}
      </div>

      <ul className="chat" ref={listRef}>
        {chatDialog.map((dialog, i) => {
          return (
            <DialogBubble
              key={i}
              from={dialog.from}
              text={dialog.text}
              citations={dialog.citations}
              links={dialog.links}
            />
          );
        })}

        {isThinking && (
          <DialogBubble key={"thinking"} from={"thinking"} text={null} />
        )}

        <li className="root-li" style={{ float: "left", clear: "both", listStyle: "none" }} ref={chatRef}></li>
      </ul>

      <div
        className={"chat-input is-disabled-" + (isThinking ? "true" : "false")}
      >
        <form
          className="chat-input-wrapper"
          action="#"
          onSubmit={handleChatSubmit}
        >
          <div className="chat-text-input">
            <label className="screen-reader-text" htmlFor="chat-input">
              Ask a question
            </label>
            <input
              type="text"
              id="chat-input"
              aria-label="Ask a question"
              placeholder="Ask a question…"
              disabled={isThinking}
              ref={inputRef}
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
            />
          </div>
          <button type="submit" aria-label="Send message">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5"
              />
            </svg>
          </button>
        </form>
      </div>
    </div>
  );
}

function formatChatText(text) {
  if (typeof text !== "string") return text;

  let raw = text;
  const preMatch = raw.match(/^<pre>([\s\S]*)<\/pre>$/);
  if (preMatch) {
    raw = preMatch[1];
  }

  // The answer is untrusted: indexed pages and PDFs can carry text that talks
  // the model into emitting markup, and this all ends up in
  // dangerouslySetInnerHTML. Escape first, then add only our own markup —
  // strong, lists, and the link tokens, which are built with escaped values.
  raw = escapeHtml(raw);

  raw = raw.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");

  const lines = raw.split(/\r?\n/);
  const BULLET = /^[-*]\s+/;
  const NUMBERED = /^\d+[.)]\s+/;

  // Build a small tree first. Bullets that follow a numbered item belong
  // *inside* that item — a numbered section with points under it. Closing the
  // <ol> and starting a fresh one instead (the old behaviour) restarted the
  // numbering, so every section rendered as "1.".
  const nodes = [];
  const stack = []; // open lists, outermost first

  const currentItem = () => {
    const list = stack[stack.length - 1];
    return list && list.items.length ? list.items[list.items.length - 1] : null;
  };

  const openList = (type, parentItem) => {
    const list = { node: "list", type: type, items: [] };
    if (parentItem) {
      parentItem.children = list;
    } else {
      nodes.push(list);
    }
    stack.push(list);
    return list;
  };

  lines.forEach((line) => {
    const trimmed = line.trim();
    const isBullet = BULLET.test(trimmed);
    const isNumbered = NUMBERED.test(trimmed);

    if (isBullet || isNumbered) {
      const type = isBullet ? "ul" : "ol";
      const text = trimmed.replace(isBullet ? BULLET : NUMBERED, "");
      if (!text.length) {
        return;
      }

      const top = stack[stack.length - 1];

      if (!top) {
        openList(type, null);
      } else if (top.type !== type) {
        const parent = stack[stack.length - 2];
        if (parent && parent.type === type) {
          // Closing a nested list and continuing the one that contains it.
          stack.pop();
        } else if (type === "ul") {
          // Points under the numbered item we're already inside.
          openList(type, currentItem());
        } else {
          stack.length = 0;
          openList(type, null);
        }
      }

      stack[stack.length - 1].items.push({ text: text, children: null });
      return;
    }

    // A blank line between items is spacing, not the end of the list — models
    // routinely separate numbered items that way. Only real text closes it.
    if (trimmed.length === 0) {
      return;
    }

    stack.length = 0;
    nodes.push({ node: "para", text: trimmed });
  });

  const renderList = (list) => {
    let out = "<" + list.type + ">";
    list.items.forEach((item) => {
      out += "<li>" + item.text + (item.children ? renderList(item.children) : "") + "</li>";
    });
    return out + "</" + list.type + ">";
  };

  let html = "";
  nodes.forEach((node) => {
    html += node.node === "para" ? "<p>" + node.text + "</p>" : renderList(node);
  });

  return html;
}

// Citations are pre-numbered + de-duped server-side (footnote N = index+1).
// Keep order; just normalize the link + label per entry.
function normalizeCitations(citations) {
  if (!Array.isArray(citations)) return [];
  return citations.map((c, i) => ({
    url: (c && (c.page_url || c.pdf_url)) || "",
    title: (c && (c.title || c.filename)) || "Source " + (i + 1),
  }));
}

function escapeAttr(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// Everything we hold a trustworthy URL for: the cited sources, plus the items
// the server put in this request's prompt. The latter is what lets an answer
// that names several pages end up with all of them linked, even when the model
// only wrote a markdown link for some.
function linkTargets(sources, links) {
  const targets = sources.slice();
  const seen = {};
  targets.forEach((t) => {
    if (t.url) seen[t.url] = true;
  });

  (Array.isArray(links) ? links : []).forEach((l) => {
    const url = (l && l.url) || "";
    const title = (l && l.title) || "";
    if (!url || !title || seen[url]) return;
    seen[url] = true;
    targets.push({ url, title });
  });

  return targets;
}

// Fold the punctuation that differs between a WordPress title and the model's
// retyping of it: curly vs straight quotes, en/em dashes, odd whitespace. Every
// substitution is one character for one character, so offsets stay valid
// against the original string.
function canonical(s) {
  return String(s)
    .toLowerCase()
    .replace(/[‘’‚‛′]/g, "'")
    .replace(/[“”„‟″]/g, '"')
    .replace(/[‐‑‒–—―−]/g, "-")
    .replace(/\s/g, " ");
}

// Index of a whole-word occurrence, so "Blog" doesn't match inside "Bloggers".
function indexOfWhole(haystack, needle) {
  const isWord = (c) => /[a-z0-9]/i.test(c);
  let from = 0;
  for (;;) {
    const idx = haystack.indexOf(needle, from);
    if (idx === -1) return -1;
    const before = idx === 0 ? "" : haystack.charAt(idx - 1);
    const after = haystack.charAt(idx + needle.length);
    if (!isWord(before) && !isWord(after)) return idx;
    from = idx + 1;
  }
}

// Wrap the first verbatim mention of each known title in a [[link:i]] token
// (expanded to a link later). Longest titles first so nested names match
// correctly; URLs come from our own index, so no fabricated links. Titles the
// model already linked itself are gone by now — extractLinks() replaced them
// with tokens — so nothing gets linked twice.
// Models shorten titles: "Commonwealth Launches Inclusive Product Design
// Resource Center for Fintech and Financial Services" comes back as "Inclusive
// Product Design Resource Center". An exact search finds nothing, so fall back
// to the longest run of consecutive title words that does appear. Bounded so a
// couple of common words can't drag in a link: at least 4 words and 25
// characters, which is long enough to be a real reference to that document.
const MIN_PARTIAL_WORDS = 4;
const MIN_PARTIAL_CHARS = 25;

// "&" and "and" are swapped freely by the model — it wrote "Impact & Learnings"
// for a title reading "Impact and Learnings". Folding one into the other changes
// the string length, so this also returns a map from each folded character back
// to its offset in the original text, letting a match be sliced out accurately.
function foldForMatch(text) {
  const canon = canonical(text);
  let out = "";
  const map = [];

  for (let i = 0; i < canon.length; i++) {
    const ch = canon.charAt(i);
    if (ch === "&") {
      for (let k = 0; k < 3; k++) {
        out += "and".charAt(k);
        map.push(i);
      }
    } else {
      out += ch;
      map.push(i);
    }
  }

  return { text: out, map: map };
}

// Translate a match in folded coordinates back to the original string.
function unfoldRange(fold, index, length) {
  const start = fold.map[index];
  const last = fold.map[index + length - 1];
  return { index: start, length: last - start + 1 };
}

function longestTitleRun(haystack, title) {
  const words = title.split(" ").filter(Boolean);
  if (words.length < MIN_PARTIAL_WORDS) return null;

  for (let size = words.length - 1; size >= MIN_PARTIAL_WORDS; size--) {
    for (let start = 0; start + size <= words.length; start++) {
      const run = words.slice(start, start + size).join(" ");
      if (run.length < MIN_PARTIAL_CHARS) continue;
      const idx = indexOfWhole(haystack, run);
      if (idx !== -1) return { index: idx, length: run.length, run: run };
    }
  }
  return null;
}

function linkifyTitles(text, targets) {
  let out = typeof text === "string" ? text : "";
  const order = targets
    .map((s, i) => ({ i, s }))
    .filter((x) => x.s.url && x.s.title && x.s.title.length >= 5)
    .sort((a, b) => b.s.title.length - a.s.title.length);

  // Work out each target's match first, so an ambiguous run — one that fits
  // two different documents equally — can be dropped rather than linked to
  // whichever happened to sort first.
  const matches = [];
  const fold = foldForMatch(out);
  order.forEach(({ i, s }) => {
    const title = foldForMatch(s.title).text;

    const exact = indexOfWhole(fold.text, title);
    if (exact !== -1) {
      const range = unfoldRange(fold, exact, title.length);
      matches.push({ i, index: range.index, length: range.length, key: title, exact: true });
      return;
    }

    const partial = longestTitleRun(fold.text, title);
    if (partial) {
      const range = unfoldRange(fold, partial.index, partial.length);
      matches.push({ i, index: range.index, length: range.length, key: partial.run, exact: false });
    }
  });

  const ambiguous = {};
  matches.forEach((m) => {
    if (!m.exact) ambiguous[m.key] = (ambiguous[m.key] || 0) + 1;
  });

  // Replace back-to-front so earlier offsets stay valid.
  const labels = {};
  matches
    .filter((m) => m.exact || ambiguous[m.key] === 1)
    .sort((a, b) => b.index - a.index)
    .forEach((m) => {
      const before = out.slice(0, m.index);
      const after = out.slice(m.index + m.length);
      // Don't nest inside a token we already placed.
      if (/\[\[[a-z]+:\d+\]\]$/.test(before) || /^\]\]/.test(after)) return;
      // Keep the words the model actually wrote as the link text — on a
      // partial match, swapping in the full title mid-sentence reads badly.
      labels[m.i] = out.slice(m.index, m.index + m.length);
      out = before + "[[link:" + m.i + "]]" + after;
    });

  return { text: out, labels: labels };
}

// Model output is not trusted enough to emit arbitrary URI schemes: only
// http(s) and site-relative paths may become links.
function safeUrl(url) {
  const u = String(url || "")
    .trim()
    .replace(/[.,;:!?]+$/, "");
  if (/^https?:\/\/\S+$/i.test(u)) return u;
  if (/^\/[^/\s]\S*$/.test(u)) return u;
  return "";
}

// Pull markdown links and bare URLs out into [[a:N]] tokens before anything
// else runs, so the line formatter and the title linkifier can't mangle them
// (and can't nest an anchor inside an anchor).
function extractLinks(text) {
  const links = [];
  let out = typeof text === "string" ? text : "";

  // The URL part tolerates one level of nested parens, so a link whose target
  // contains them is consumed whole rather than leaving a stray ")" behind.
  out = out.replace(
    /\[([^\]\n]+)\]\(([^()\s]*(?:\([^()\s]*\)[^()\s]*)*)\)/g,
    (m, label, url) => {
      const href = safeUrl(url);
      if (!href) return label;
      links.push({ href, label });
      return "[[a:" + (links.length - 1) + "]]";
    }
  );

  out = out.replace(/(^|[\s(])(https?:\/\/[^\s<>()[\]"']+)/gi, (m, lead, url) => {
    // Sentence punctuation after a bare URL belongs to the sentence, not the link.
    const trailing = (url.match(/[.,;:!?]+$/) || [""])[0];
    const bare = trailing ? url.slice(0, -trailing.length) : url;
    const href = safeUrl(bare);
    if (!href) return m;
    links.push({
      href,
      label: href.replace(/^https?:\/\//i, "").replace(/\/$/, ""),
    });
    return lead + "[[a:" + (links.length - 1) + "]]" + trailing;
  });

  return { text: out, links };
}

// Render answer HTML: explicit links, inline page links + footnote superscripts.
function renderAnswerHtml(text, sources, links) {
  const targets = linkTargets(sources, links);
  const extracted = extractLinks(text);
  const linkified = linkifyTitles(extracted.text, targets);
  const html = formatChatText(linkified.text);
  if (typeof html !== "string") return html;

  return html
    .replace(/\[\[a:(\d+)\]\]/g, (m, iStr) => {
      const link = extracted.links[parseInt(iStr, 10)];
      if (!link) return "";
      return (
        '<a href="' +
        escapeAttr(link.href) +
        '" target="_blank" rel="noopener noreferrer">' +
        escapeHtml(link.label) +
        "</a>"
      );
    })
    .replace(/\[\[cite:(\d+)\]\]/g, (m, nStr) => {
      const n = parseInt(nStr, 10);
      const src = sources[n - 1];
      if (src && src.url) {
        return (
          '<sup class="chat-cite"><a href="' +
          escapeAttr(src.url) +
          '" target="_blank" rel="noopener noreferrer">' +
          n +
          "</a></sup>"
        );
      }
      return '<sup class="chat-cite">' + n + "</sup>";
    })
    .replace(/\[\[link:(\d+)\]\]/g, (m, iStr) => {
      const idx = parseInt(iStr, 10);
      const src = targets[idx];
      // The text the model wrote, which may be a shortened form of the title.
      const label = linkified.labels[idx] || (src && src.title) || "";
      if (src && src.url) {
        return (
          '<a href="' +
          escapeAttr(src.url) +
          '" target="_blank" rel="noopener noreferrer">' +
          escapeHtml(label) +
          "</a>"
        );
      }
      return escapeHtml(label);
    });
}

function DialogBubble({ from, text, citations, links }) {
  // Flat disclaimer note — rendered plainly, not as a chat bubble.
  if (from === "disclaimer") {
    return <li className="root-li disclaimer-note">{text}</li>;
  }

  const sources = normalizeCitations(citations);
  const answerHtml = renderAnswerHtml(text, sources, links);

  return (
    <li className={"root-li from-" + from}>
      {(from === "chatbot" || from === "thinking") && (
        <div>
          <Avatar size={24} />
        </div>
      )}
      {from === "thinking" ? (
        <div className="text-wrapper">
          <svg height="20" width="40">
            <circle className="dot" cx="10" cy="10" r="3" />
            <circle className="dot" cx="20" cy="10" r="3" />
            <circle className="dot" cx="30" cy="10" r="3" />
          </svg>
        </div>
      ) : (
        <div className="text-wrapper">
          <div dangerouslySetInnerHTML={{ __html: answerHtml }}></div>

          {sources.length > 0 && (
            <div className="chat-sources">
              <span className="chat-sources-label">Sources:</span>
              <ol>
                {sources.map((s, i) => (
                  <li key={i} value={i + 1}>
                    {s.url ? (
                      <a href={s.url} target="_blank" rel="noopener noreferrer">
                        {s.title}
                      </a>
                    ) : (
                      s.title
                    )}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}
    </li>
  );
}
